import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, CardBody } from 'reactstrap';
import cs_analysis_fetch from '../../utils/cs_analysis_fetch';
import { format_datetime_dd_mmm_yy as fmt_datetime } from '../../utils/format_date_dd_mmm_yy';
import fmt_date from '../../utils/format_date_dd_mmm_yy';
import { decision_message, expiry_label, group_offers_by_meeting, short_quote, without_offer } from './meeting_commitments_helpers';

/**
 * "From your meetings" — the rep's own promises from recent client calls, as
 * cs-analysis heard them (docs/design-meeting-commitments.md §3). The same
 * offers the Google Chat DM carries; the CSP is the durable copy, so a missed
 * DM loses nothing. Each offer has two buttons: *Add to my tasks* creates the
 * task on the rep (and the Tasks list below refreshes), *Not needed* drops it.
 * Untouched offers go quiet after the api's expireDays. Nothing else exists
 * until the rep decides — no badge, no count anywhere else.
 *
 * Props: rep_email (admin impersonation → ?rep_email=), store_id (narrow to
 * one client), on_changed (called after an accept so the parent reloads).
 */
const MeetingCommitments = ({ rep_email = null, store_id = null, on_changed, admin = false, reps = [] }) => {
    // An admin who is not a CS rep has no offers of their own: pick a rep to see theirs (the api allows admins to read and decide for any rep).
    const [picked_rep, set_picked_rep] = useState('');
    const effective_rep = rep_email || (admin ? picked_rep : '') || null;
    const [offers, set_offers] = useState(null);
    const [expire_days, set_expire_days] = useState(null);
    const [err, set_err] = useState(null);
    const [busy_id, set_busy_id] = useState(null);
    const [note, set_note] = useState(null);

    const load = useCallback(async () => {
        set_err(null);
        try {
            const query = {};
            if (effective_rep) query.rep_email = effective_rep;
            if (store_id) query.storeId = store_id;
            const r = await cs_analysis_fetch({ path: '/api/v1/meeting-commitments/mine', query });
            set_offers(r && Array.isArray(r.offers) ? r.offers : []);
            set_expire_days(r && r.expireDays ? r.expireDays : null);
        } catch (e) {
            set_err(e.message || String(e));
            set_offers([]);
        }
    }, [effective_rep, store_id]);

    useEffect(() => { load(); }, [load]);

    const decide = useCallback(async (id, action) => {
        if (busy_id) return;
        set_busy_id(id);
        set_err(null);
        try {
            const r = await cs_analysis_fetch({ path: `/api/v1/meeting-commitments/${encodeURIComponent(id)}/decide`, method: 'POST', body: { action } });
            set_offers((prev) => without_offer(prev, id));
            set_note(decision_message(action, r && r.outcome));
            if (action === 'accept' && on_changed) on_changed();
        } catch (e) {
            set_err(e.message || String(e));
        } finally {
            set_busy_id(null);
        }
    }, [busy_id, on_changed]);

    const rep_picker = admin && !rep_email && reps.length > 0 ? (
        <select value={picked_rep} onChange={(e) => { set_note(null); set_picked_rep(e.target.value); }} style={{ fontSize: 12, marginLeft: 8 }} data-testid='meeting-offers-rep'>
            <option value=''>Show a rep's offers…</option>
            {reps.filter((r) => r.email).map((r) => <option key={r.email} value={r.email}>{r.name}</option>)}
        </select>
    ) : null;
    if (offers === null && !rep_picker) return null;
    const groups = group_offers_by_meeting(offers || []);
    if (groups.length === 0 && !note && !err && !rep_picker) return null;

    return (
        <Card style={{ marginBottom: 12, borderLeft: '4px solid #8e44ad' }}>
            <CardBody style={{ padding: 12 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 11, color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.5 }}>From {effective_rep && admin ? `${effective_rep.split('@')[0]}'s` : 'your'} meetings{rep_picker}</div>
                    <div style={{ fontSize: 12, color: '#7f8c8d' }}>
                        Things you said you would do on recent calls, in your own words. Nothing is created unless you add it
                        {expire_days ? `; untouched offers go quiet after ${expire_days} days` : ''}.
                    </div>
                </div>
                {err ? <div style={{ color: '#c0392b', fontSize: 12, marginTop: 6 }}>{err}</div> : null}
                {admin && effective_rep && offers && offers.length === 0 && !note ? <div style={{ fontSize: 12, color: '#7f8c8d', marginTop: 6 }}>No open offers for this rep right now.</div> : null}
                {note ? <div style={{ color: '#27ae60', fontSize: 12, marginTop: 6 }}>{note}</div> : null}
                {groups.map((g) => (
                    <div key={g.key} style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>
                            {g.storeId ? (
                                <a href={`/home/store_dashboard/${encodeURIComponent(g.storeId)}`} target='_blank' rel='noreferrer noopener'>{g.clientName}</a>
                            ) : g.clientName}
                            <span style={{ fontSize: 12, color: '#7f8c8d', fontWeight: 400, marginLeft: 8 }}>call on {g.meetingAt ? fmt_datetime(g.meetingAt) : '—'}</span>
                        </div>
                        {g.offers.map((o) => (
                            <div key={o.id} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '8px 0', borderTop: '1px solid #f0f2f5' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 500 }}>
                                        {o.summary}
                                        {o.dueDate ? <span style={{ fontSize: 12, color: '#c0392b', marginLeft: 8 }}>due {fmt_date(o.dueDate)}</span> : null}
                                        {o.module ? <span style={{ fontSize: 11, color: '#7f8c8d', marginLeft: 8 }}>{o.module}</span> : null}
                                    </div>
                                    <div style={{ fontSize: 12, color: '#566573', fontStyle: 'italic', marginTop: 2 }} title={o.quote}>“{short_quote(o.quote)}”</div>
                                    <div style={{ fontSize: 11, color: '#95a5a6', marginTop: 2 }}>{expiry_label(o.expiresAt)}</div>
                                </div>
                                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                    <Button size='sm' color='primary' disabled={busy_id === o.id} onClick={() => decide(o.id, 'accept')}>Add to my tasks</Button>
                                    <Button size='sm' outline color='secondary' disabled={busy_id === o.id} onClick={() => decide(o.id, 'ignore')}>Not needed</Button>
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </CardBody>
        </Card>
    );
};

export default MeetingCommitments;
